import { FC, PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Box, Container, HStack, Image, rem, ToastContainerSyle } from '@bonusly/design-system';
import { useCurrentCompany } from '@bonusly/who-am-i';

import logo from '../../assets/images/bonusly-logo.png';
import { SpoofingWarning } from '../developer/spoofing-warning';
import { useSeasonalLogo } from '../seasonal/use-seasonal-logo';
import { useSpoofing } from './hooks/useSpoofing';

const CompanyLogo: FC = () => {
	const company = useCurrentCompany();
	const { t } = useTranslation('monarch');
	const { logoUrl: seasonalLogo } = useSeasonalLogo();

	// Determine which logo to use: seasonal > company > default
	const logoConfig = useMemo(() => {
		const logoSrc = seasonalLogo || company?.logo_url || logo;
		const logoAlt = seasonalLogo
			? t('links.company_logo', { name: 'Bonusly 🎃' })
			: company?.logo_url
				? t('links.company_logo', { name: company.name })
				: t('links.company_logo', { name: 'Bonusly' });

		return { logoSrc, logoAlt };
	}, [seasonalLogo, company?.logo_url, company?.name, t]);

	return <Image alt={logoConfig.logoAlt} h="full" objectFit="contain" src={logoConfig.logoSrc} />;
};

export const NavlessLayout: FC<PropsWithChildren> = ({ children }) => {
	const { data } = useSpoofing();

	return (
		<Box bg="bg.subtle" display="flex" flexDirection="column" minH="100vh" minW={rem(370)}>
			<Box
				bg="bg.surface"
				borderBottomColor="border.default"
				borderBottomStyle="solid"
				borderBottomWidth="1px"
				position="sticky"
				top="0"
				w="100%"
				zIndex="docked"
			>
				{data?.is_spoofing && !data?.is_demoing && <SpoofingWarning />}

				<ToastContainerSyle style={{ 'padding-top': rem(64) }} />

				<Container maxW={'100%'} px={4}>
					<HStack as={Link} to="/" h={16} align="center" maxW={72}>
						<Box h={rem(46)}>
							<CompanyLogo />
						</Box>
					</HStack>
				</Container>
			</Box>

			<Box display="flex" flexDirection="column" flexGrow={1} pos="relative" zIndex="base">
				{children}
			</Box>
		</Box>
	);
};
